import {observer} from "mobx-react-lite";
import React from "react";
import EventBooking from "../../../stores/domainStore/pages/models/eventBooking";
import {EventBookingStatus} from "../../../shared/enums/eventBookingStatus";
import EventBookingsTable from "../eventBookingsTable/eventBookingsTable";
import ResidentData from "../../../stores/domainStore/pages/models/residentData";
import BookingNotes from "../../../stores/domainStore/pages/models/bookingNotes";
import {useStores} from "../../../stores/useStores";
import {EventPaidType} from "../../../shared/enums/eventPaidType";
import {EventBookingSource} from "../../../shared/enums/eventBookingSource";

const EventBookingsConfirmedTable: React.FC = () => {
  const {
    domainStore: {eventsDomainStore},
    uiStore: {eventsUiStore}
  } = useStores();

  const eventDetails = eventsDomainStore.editableEvent;

  const isFixedCapacityEvent =
    !!eventDetails?.bookingSpacesDetails.totalSpace.number;

  /**
   * Bookings can only be modified if
   * - Event is not passed the booking deadline when there is a release date, or
   * - Event is not passed the end date when there is no release date
   */
  const isEditEnabled =
    eventDetails.releaseDate.date !== null
      ? !eventDetails.isPassedBookingDeadline
      : !eventDetails.isPassedEndDate;

  const generateTransientRow = (
    id: string,
    index: number,
    onBuild: (eventBooking: EventBooking) => void
  ): EventBooking => {
    const generatedRow = new EventBooking(
      id,
      index,
      EventBookingSource.Staff,
      EventBookingStatus.Confirmed,
      new ResidentData(),
      new BookingNotes(),
      "personId",
      "personId"
    );
    onBuild(generatedRow);
    return generatedRow;
  };

  const calculateEventBookingData = (): EventBooking[] => {
    const eventBookings = eventsUiStore.confirmedEventBookings.slice();
    if (
      !eventBookings.length &&
      eventDetails.bookingSpacesDetails.totalBookings
    ) {
      // We haven't yet loaded the event bookings, we must fake some data
      for (
        let i = 0;
        i < eventDetails.bookingSpacesDetails.totalBookings;
        i++
      ) {
        eventBookings.push(
          generateTransientRow(`booking-${i}`, i + 1, (b) => {
            b.isLoadingResidentDetails = true;
            b.isSourceShimmer = true;
          })
        );
      }
    }

    const maxCapacityReached =
      isFixedCapacityEvent &&
      eventDetails?.bookingSpacesDetails.capacityRemaining === 0;

    if (!maxCapacityReached && isEditEnabled) {
      eventBookings.push(
        generateTransientRow(
          "confirmed-addResident",
          eventBookings.length + 1,
          (b) => {
            b.isAddResident = true;
            b.source = eventDetails.bookingSpacesDetails
              .reservedCapacityRemaining
              ? EventBookingSource.Staff
              : EventBookingSource.None;
          }
        )
      );
    }

    if (eventDetails.bookingSpacesDetails.reservedCapacityRemaining) {
      for (
        let i = !isEditEnabled ? 0 : 1; // add resident row
        i < eventDetails.bookingSpacesDetails.reservedCapacityRemaining;
        i++
      ) {
        eventBookings.push(
          generateTransientRow(
            `reserved-${i}`,
            eventBookings.length + 1,
            (b) => {
              b.isReservedRow = true;
            }
          )
        );
      }
    }

    if (eventDetails.bookingSpacesDetails.capacityRemaining && isEditEnabled) {
      let reservedCapacityRemaining =
        eventDetails.bookingSpacesDetails.reservedCapacityRemaining ?? 0;

      // If we don't have reservedCapacityRemaining, we must remove 1 row for add resident row
      if (isEditEnabled && reservedCapacityRemaining === 0) {
        reservedCapacityRemaining = 1;
      }

      const openCapacityRemaining =
        eventDetails.bookingSpacesDetails.capacityRemaining -
        reservedCapacityRemaining;

      for (let i = 0; i < openCapacityRemaining; i++) {
        eventBookings.push(
          generateTransientRow(
            `capacity-${i}`,
            eventBookings.length + 1,
            (b) => {
              b.isCapacityRow = true;
            }
          )
        );
      }
    }

    return eventBookings;
  };

  return (
    <EventBookingsTable
      eventBookings={calculateEventBookingData()}
      type={EventBookingStatus.Confirmed}
      isPaidEvent={
        eventDetails?.priceDetails?.paidType.text === EventPaidType.fixed
      }
    />
  );
};
export default observer(EventBookingsConfirmedTable);
